import React, { useEffect, useState } from 'react'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import UserService from '../../../services/User/index'
import { ServerError } from '../../../schemas/Error'

const texts = {
  title: 'Recuperar contraseña',
  message: 'Ingresa tu correo electrónico. Si está registrado, recibirás un enlace válido por 12 horas.',
  emailLabel: 'Correo',
  emailRequired: 'El correo es requerido.',
  cancel: 'Cancelar',
  confirm: 'Enviar enlace',
  success: 'Si el correo está registrado, recibirás un enlace válido por 12 horas.',
  genericError: 'No se pudo procesar la solicitud. Intente nuevamente.',
  rateLimitError: 'Demasiados intentos. Intente nuevamente más tarde.',
}

interface Props {
  open: boolean
  initialEmail: string
  onClose: () => void
}

/** Requests a password reset email from the public login page. */
export default function ForgotPasswordDialog(props: Props) {
  const [email, setEmail] = useState(props.initialEmail)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (props.open) {
      setEmail(props.initialEmail)
    }
  }, [props.open, props.initialEmail])

  const handleClose = () => {
    if (loading) {
      return
    }
    props.onClose()
  }

  async function handleConfirm() {
    const trimmedEmail = email.trim()

    if (trimmedEmail === '') {
      toast.error(texts.emailRequired)
      return
    }

    setLoading(true)

    try {
      const userService = new UserService()
      await userService.forgotPassword(trimmedEmail)
      props.onClose()
      toast.success(texts.success)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError.response?.status === 429) {
          toast.error(texts.rateLimitError)
          return
        }
        if (serverError.response?.data?.message) {
          toast.error(serverError.response.data.message)
          return
        }
      }
      toast.error(texts.genericError)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{texts.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {texts.message}
        </Typography>
        <TextField
          variant="outlined"
          margin="normal"
          fullWidth
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          label={texts.emailLabel}
          name="forgotPasswordEmail"
          type="email"
          autoFocus
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default" disabled={loading}>
          {texts.cancel}
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={loading}>
          {texts.confirm}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
